import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ReactSortable } from 'react-sortablejs'
import { Button, FormErrorMessage, Input, ModalProps, Select, formatHookFormErrors, useModal } from '@dashboard/ui'
import { ItemType, ResourceState, StoreItemBonus, StoreItemRead } from '@/api/dashboard'
import { SkuImageName } from '@/layouts/game-items/components/SkuImageName'
import { SelectSkuModal } from '@/layouts/components/SelectSkuModal'
import {
  ButtonIcon,
  CheckBoxContainer,
  FieldGroup,
  FieldSection,
  InputExtraMark,
  Table,
  TableCell,
  TableRow,
} from '@/ui'
import { DotsGridSix, Trash } from '@/icons'
import { dashboardClient } from '@/api'
import { SKUItem } from '@/api/types'
import { BenefitType } from '@/types'
import { useFormContext } from 'react-hook-form'
import { useGenerateCompanyPath } from '@/libs'
import { ErrorMessage } from '@hookform/error-message'

export const BonusItemMultiBenefitEditor = () => {
  const { companyId, gameId } = useGenerateCompanyPath()
  const {
    formState: { errors },
    register,
    setValue,
    watch,
    setFocus,
  } = useFormContext<StoreItemRead>()
  const { t } = useTranslation()
  const [items, setItems] = useState<SKUItem[]>([])

  const bonusSettings = watch('bonus_items_settings')
  const bonusItems = bonusSettings?.items || []

  const loadItems = async () => {
    const ids = bonusItems
      .map(it => it.id)
      .sort()
      .join(',')

    if (!ids) {
      return
    }

    const { data } = await dashboardClient.v1.getItems({
      companyId,
      gameId,
      state: ResourceState.Active,
      ids: ids,
    })
    setItems(data)
  }

  useEffect(() => {
    loadItems()
  }, [bonusItems])

  const openPicker = useModal<ModalProps>(({ ...rest }) => (
    <SelectSkuModal
      {...rest}
      size="sm"
      title={t('game-item-edit-dialog.bundle.add-items')}
      buttonText={t('apply')}
      types={[ItemType.Item, ItemType.Currency, ItemType.Bundle]}
      picked={bonusItems.map(it => items.find(itemRead => itemRead.id == it.id)).filter(it => !!it)}
      onSubmit={items => {
        const newItems = []
        for (const it of items) {
          if (!bonusItems.find(bonusItem => bonusItem.id == it.id)) {
            newItems.push({ id: it.id })
          }
        }

        setValue(`bonus_items_settings.items`, [...bonusItems, ...newItems], { shouldDirty: true })

        rest?.onClose?.()
      }}
    />
  ))

  const renderBonusPercentOrFixed = (item: SKUItem, storeItemBonus: StoreItemBonus, idx: number) => {
    if (item.type === ItemType.Bundle || !item.is_stackable) {
      return null
    }

    const bonusType = storeItemBonus.quantity_fixed != null ? BenefitType.fixed : BenefitType.percent

    return (
      <>
        <TableCell width={160} className="select-none">
          <Select
            value={bonusType}
            items={[
              {
                children: t('store.item.bonus-type.percent'),
                value: BenefitType.percent,
              },
              { children: t('store.item.bonus-type.fixed'), value: BenefitType.fixed },
            ]}
            onChange={v => {
              if (v == BenefitType.percent) {
                setValue(`bonus_items_settings.items.${idx}.quantity_fixed`, null)
                setValue(`bonus_items_settings.items.${idx}.quantity_percent`, 0, { shouldDirty: true })
                setTimeout(
                  () => setFocus(`bonus_items_settings.items.${idx}.quantity_percent`, { shouldSelect: true }),
                  50,
                )
              } else {
                setValue(`bonus_items_settings.items.${idx}.quantity_fixed`, 0, { shouldDirty: true })
                setValue(`bonus_items_settings.items.${idx}.quantity_percent`, null)
                setTimeout(
                  () => setFocus(`bonus_items_settings.items.${idx}.quantity_fixed`, { shouldSelect: true }),
                  50,
                )
              }
            }}
          />
        </TableCell>
        <TableCell width={100} className="select-none">
          {bonusType == BenefitType.percent ? (
            <Input
              key="percent"
              extraLeft={{
                icon: <InputExtraMark>%</InputExtraMark>,
              }}
              type={'number'}
              {...register(`bonus_items_settings.items.${idx}.quantity_percent`, {
                required: t('validation.required'),
                min: { value: 1, message: t('validation.min', { field: t('store.item.bonus'), value: 1 }) },
                max: { value: 1000, message: t('validation.max', { field: t('store.item.bonus'), value: 1000 }) },
              })}
            />
          ) : (
            <Input
              key="fixed"
              type={'number'}
              {...register(`bonus_items_settings.items.${idx}.quantity_fixed`, {
                required: t('validation.required'),
                min: { value: 1, message: t('validation.min', { field: t('store.item.bonus_fixed'), value: 1 }) },
              })}
            />
          )}
        </TableCell>
      </>
    )
  }

  const renderBonusRow = (storeItemBonus: StoreItemBonus, idx: number) => {
    if (!storeItemBonus) {
      return null
    }
    const item = items.find(it => it.id == storeItemBonus.id)

    return (
      <>
        <TableRow variant="header" disableFocus className="mx-0 h-[52px] px-0">
          <TableCell width={30}>{<DotsGridSix className="cursor-move" />}</TableCell>
          <TableCell width="100%" className="truncate">
            {item && <SkuImageName item={item} hideQuantity={true} />}
          </TableCell>

          {item && renderBonusPercentOrFixed(item, storeItemBonus, idx)}

          <TableCell width={40} className="px-0">
            <ButtonIcon
              variant="tertiary"
              color="danger"
              onClick={() => {
                const newItems = bonusItems.filter((_, i) => i !== idx)
                setValue('bonus_items_settings.items', newItems, { shouldDirty: true })
              }}
            >
              <Trash />
            </ButtonIcon>
          </TableCell>
        </TableRow>
        <ErrorMessage
          name={`bonus_items_settings.items.${idx}.quantity_fixed`}
          errors={errors}
          render={({ message }) => <FormErrorMessage className="mx-2 mt-1">{message}</FormErrorMessage>}
        />
        <ErrorMessage
          name={`bonus_items_settings.items.${idx}.quantity_percent`}
          errors={errors}
          render={({ message }) => <FormErrorMessage className="mx-2 mt-1">{message}</FormErrorMessage>}
        />
      </>
    )
  }

  return (
    <FieldSection label={t('store-promotions.bonus-item.field-section-label')}>
      <CheckBoxContainer
        label={t('store.item.add-bonus-item')}
        checked={!!bonusSettings}
        onChange={v => {
          if (!v.target.checked) {
            setValue('bonus_items_settings', null, { shouldDirty: true })
            setValue('bonus_badge', null, { shouldDirty: true })
          } else {
            setValue('bonus_items_settings', { items: [] }, { shouldDirty: true })
            setTimeout(() => setFocus(`bonus_badge`), 100)
          }
        }}
      >
        <FieldGroup label={t('store.item.bonus.badge')}>
          <Input
            {...register('bonus_badge', {
              required: false,
            })}
            {...formatHookFormErrors(errors, 'bonus_badge')}
          />
        </FieldGroup>

        <Table className="mt-0 overflow-hidden rounded-md border border-border-primary">
          <TableRow variant="header" className="mx-0 h-[42px] border-b border-border-secondary px-0">
            <TableCell width={30} />
            <TableCell width="100%">{t('store.item.bonus.name')}</TableCell>
            <TableCell width={160}>{t('store.item.bonus.quantity_type')}</TableCell>
            <TableCell width={100}>{t('store.item.bonus.value')}</TableCell>
            <TableCell width={40} />
          </TableRow>
          {bonusItems?.length > 0 && (
            <ReactSortable
              dragClass="cursor-move"
              filter=".select-none"
              animation={200}
              delay={2}
              preventOnFilter={false}
              list={(bonusItems || []) as { id: string }[]}
              setList={v => {
                for (let i = 0; i < v.length; i++) {
                  if (v[i].id != bonusItems[i].id) {
                    setValue('bonus_items_settings.items', v, { shouldDirty: true })
                    break
                  }
                }
              }}
            >
              {bonusItems.map((it, idx) => renderBonusRow(it, idx))}
            </ReactSortable>
          )}
          <TableRow variant="header" className="mx-0  px-0">
            <TableCell>
              <Button onClick={() => openPicker()} variant="outline" color="primary">
                {t('store.item.bonus.add-items')}
              </Button>
            </TableCell>
          </TableRow>
        </Table>
      </CheckBoxContainer>
    </FieldSection>
  )
}
